import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerDetails, getClientList, updateCustomerDetails } from "./CustomerView.actions";
import { getSensitiveFields } from "../ClientView/ClientView.actions";
import { CUSTOMER_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/CustomerViewConstants";
import Spinner from "components/Spinner";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { ColumnTypes } from "commons/table/Constants";
import { FilterTypes } from "commons/table/Constants";
import './CustomerView.css';
import { DEFAULT_PAGE_SIZE } from "../CustomerList/Customer.reducer";
import CreateClientModal from "./CreateClientModal";
import ConfirmationModal from "components/ConfirmationModal";
import { FaEye } from "react-icons/fa";
import { formatPhoneNumber } from "services/UtilityService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import { useConfigForm } from "../configTool/useConfigForm";
import { validateCustomerEditForm, normalizePhoneDigits } from "../configTool/configFormValidators";
import {
  ConfigReadOnlyBanner,
  ConfigFormLabel,
  ConfigFormSwitch,
  SensitiveFieldsConfirmationBody,
} from "../configTool/ConfigFormComponents";

const CustomerView = () => {
  const { customerInfoUuid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    details: customer,
    loadingDetails,
    clients,
    loadingClients,
    clientListState,
    pages,
    totalElements,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.customerView
  );

  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [changedSensitiveFields, setChangedSensitiveFields] = useState([]);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  const initialFormValues = useMemo(() => {
    if (!customer) return null;
    return {
      ...customer,
      supportNumber: normalizePhoneDigits(customer.supportNumber),
      faxNumberInbound: normalizePhoneDigits(customer.faxNumberInbound),
      showSavingPrices: customer.showSavingPrices !== undefined ? customer.showSavingPrices : true,
      botCallingAllowed: customer.botCallingAllowed === true,
      botEntityName: customer.botEntityName ?? '',
    };
  }, [customer]);

  const {
    formState,
    originalFormState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    runValidation,
  } = useConfigForm({
    initialValues: initialFormValues,
    validate: validateCustomerEditForm,
  });

  useEffect(() => {
    dispatch(getCustomerDetails(customerInfoUuid));
    dispatch(getClientList(customerInfoUuid, clientListState.pageSize, clientListState.page, clientListState.sorted, clientListState.filtered));
    dispatch(getSensitiveFields(CUSTOMER_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, customerInfoUuid]);

  const onUpdateClientTable = (filters, sort, pageSize, page) => {
    dispatch(getClientList(customerInfoUuid, pageSize, page, sort, filters));
  };

  const handleClientCreated = () => {
    onUpdateClientTable(clientListState.filtered, clientListState.sorted, clientListState.pageSize, clientListState.page);
  };

  const handleCancel = () => {
    navigate("/admin/customers");
  };

  const proceedWithSave = () => {
    dispatch(updateCustomerDetails(customerInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CUSTOMER_DETAILS_SUCCESS") {
          navigate("/admin/customers");
        }
      }
    );
  };

  const handleSave = () => {
    setShowValidation(true);
    if (!runValidation()) {
      return;
    }

    const changedFields = sensitiveFields.filter(
      (field) => originalFormState && originalFormState[field] !== formState[field]
    );
    if (changedFields.length > 0) {
      setChangedSensitiveFields(changedFields);
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const clientColumns = [
    {
      header: "Name",
      accessor: "clientName",
      accessorKey: "clientName",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Client Abbreviation",
      accessor: "clientNameAbbreviation",
      accessorKey: "clientNameAbbreviation",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Actions",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return (
          <Link variant="primary" to={`/admin/client/${row.original.clientInfoUuid}`}>
            <FaEye /> &nbsp; View
          </Link>
        );
      },
    },
  ];

  if (loadingDetails || !customer || !formState) {
    return <Spinner />;
  }

  return (
    <Container fluid>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmSave}
        title="Confirm Save"
        body={<SensitiveFieldsConfirmationBody changedFields={changedSensitiveFields} />}
      />
      <Row>
        <Col md={12}>
          <h5>{customer.customerName}</h5>
        </Col>
      </Row>
      {isReadOnly && (
        <Row className="mt-3">
          <Col md={12}>
            <ConfigReadOnlyBanner />
          </Col>
        </Row>
      )}
      <Row>
        <Col md={12} className="mt-4">
          <div className="customer-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}
            <Card>
              <Card.Header>
                <Card.Title as="h6">Identity</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" name="programName" value={formState.programName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <Form.Label>Customer Abbreviation <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerNameAbbreviation} disabled/>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                      <Form.Group>
                      <ConfigFormLabel label="Logo URL" fieldName="logoUrl" sensitiveFields={sensitiveFields} />
                      <Form.Control type="text" name="logoUrl" value={formState.logoUrl} onChange={handleInputChange} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
        <Col md={12} className="mt-4">
          <div className="customer-details-wrapper">
            <Card>
              <Card.Header>
                <Card.Title as="h6">Contact</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      <ConfigFormLabel label="Support Number" fieldName="supportNumber" sensitiveFields={sensitiveFields} />
                      <Form.Control type="text" name="supportNumber" value={formatPhoneNumber(formState.supportNumber)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} isInvalid={showValidation && !!errors.supportNumber} />
                      {showValidation && <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <ConfigFormLabel label="Inbound Fax Number" fieldName="faxNumberInbound" sensitiveFields={sensitiveFields} />
                      <Form.Control type="text" name="faxNumberInbound" value={formatPhoneNumber(formState.faxNumberInbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} isInvalid={showValidation && !!errors.faxNumberInbound} />
                      {showValidation && <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>}
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <ConfigFormLabel label="Email" fieldName="email" sensitiveFields={sensitiveFields} />
                      <Form.Control type="email" name="email" value={formState.email} onChange={handleInputChange} disabled={isReadOnly} isInvalid={showValidation && !!errors.email} />
                      {showValidation && <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>}
                    </Form.Group>
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={12}>
          <div className="customer-details-wrapper">
            <Card className="mb-3">
              <Card.Header>
                <Card.Title as="h6">Programs</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <ConfigFormSwitch
                        label="Auto Accept"
                        fieldName="isAutoAcceptEnabled"
                        checked={formState.isAutoAcceptEnabled}
                        onChange={handleCheckboxChange}
                        id="auto-accept-switch"
                        disabled={isReadOnly}
                        sensitiveFields={sensitiveFields}
                      />
                    </Col>
                    <Col md={6}>
                      <ConfigFormSwitch
                        label="Intro Fax"
                        fieldName="isIntroFaxEnabled"
                        checked={formState.isIntroFaxEnabled}
                        onChange={handleCheckboxChange}
                        id="intro-fax-switch"
                        disabled={isReadOnly}
                        sensitiveFields={sensitiveFields}
                      />
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
            <Card className="mb-3">
              <Card.Header>
                <Card.Title as="h6">Automated Calling</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <ConfigFormSwitch
                        label="Bot Calling"
                        fieldName="botCallingAllowed"
                        checked={formState.botCallingAllowed}
                        onChange={handleCheckboxChange}
                        id="bot-calling-allowed-switch"
                        disabled={isReadOnly}
                        sensitiveFields={sensitiveFields}
                      />
                    </Col>
                    {formState.botCallingAllowed && (
                      <Col md={6}>
                        <Form.Group className="d-flex align-items-center">
                          <ConfigFormLabel label={<>Name of Bot Caller <span className="text-danger">*</span></>} fieldName="botEntityName" sensitiveFields={sensitiveFields} className="mb-0 mr-3 flex-shrink-0" />
                          <Form.Control className="flex-grow-1" type="text" name="botEntityName" value={formState.botEntityName} onChange={handleInputChange} disabled={isReadOnly} isInvalid={showValidation && !!errors.botEntityName} />
                          {showValidation && <Form.Control.Feedback type="invalid">{errors.botEntityName}</Form.Control.Feedback>}
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Form>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>
                <Card.Title as="h6">Pricing</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <ConfigFormSwitch
                        label="Show Prices"
                        fieldName="showSavingPrices"
                        checked={formState.showSavingPrices}
                        onChange={handleCheckboxChange}
                        id="show-saving-prices-switch"
                        disabled={isReadOnly}
                        sensitiveFields={sensitiveFields}
                      />
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      {canManage && (
        <Row className="mt-4">
          <Col md={12} className="text-right">
            <Button variant="secondary" onClick={handleCancel} className="mr-2">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={updating || !isDirty} className="save-button">
              Save
            </Button>
          </Col>
        </Row>
      )}
      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">Clients {loadingClients ? "" : "(" + totalElements +")" } </Card.Title>
                {canManage && (
                  <Button variant="primary" size="sm" onClick={() => setShowCreateClientModal(true)}>
                    Create Client
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <ServerSideReactTable
                columnDefination={clientColumns}
                data={clients}
                loading={loadingClients}
                currentPage={clientListState.page}
                totalPages={pages}
                pageSize={clientListState.pageSize}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                totalRecords={totalElements}
                sorted={clientListState.sorted}
                filters={clientListState.filtered}
                onUpdateTableData={onUpdateClientTable}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <CreateClientModal
        show={showCreateClientModal}
        onHide={() => setShowCreateClientModal(false)}
        customerInfoUuid={customerInfoUuid}
        customerName={customer?.customerName}
        onSuccess={handleClientCreated}
      />
    </Container>
  );
};

export default CustomerView;
